import { equalisations } from 'further-ui/domain';
import { FeesEqualisationTreatment } from 'further-types/lp/equalisation';
import { round, sumBy } from 'lodash';
import { EqualisationFeesFormValues } from '../Editor/EqualiseFees/FormWrapper';

interface RecalculateFeesParams {
  value: EqualisationFeesFormValues['totalCommitmentFees'][number];
  treatment?: FeesEqualisationTreatment;
  percentage?: number;
  onChange?: (
    value: EqualisationFeesFormValues['totalCommitmentFees'][number],
  ) => void;
  overrideEqualisationSalesTaxAmountLcy?: Record<string, number>;
  useOriginalFeeValues?: boolean;
}

const useEqualiseFees = () => {
  const recalculateFees = ({
    value,
    treatment,
    percentage,
    useOriginalFeeValues,
  }: RecalculateFeesParams) => {
    if (!treatment) return value;
    const recalculatedFees = (value?.lpFees || []).map((fee) => {
      const {
        equalisationFeeExTaxLcy,
        equalisationSalesTaxAmountLcy,
        equalisationFeeIncTaxLcy,
      } = equalisations.calculateEqualisationTreatmentOfFees({
        feeExTaxLcy: useOriginalFeeValues
          ? fee.feeExTaxLcy
          : fee.equalisationFeeExTaxLcy || 0,
        salesTaxPercentage: fee.salesTaxPercentage || 0,
        feesEqualisationTreatment: treatment,
        feesEqualisationTreatmentPercentage: percentage ?? 0,
      });

      return {
        ...fee,
        equalisationFeeExTaxLcy,
        equalisationSalesTaxAmountLcy,
        equalisationFeeIncTaxLcy,
      };
    });

    const totalEqualisationFeeIncTaxLcy = sumBy(
      recalculatedFees,
      (fee: { equalisationFeeIncTaxLcy: number }) =>
        fee.equalisationFeeIncTaxLcy,
    );

    const updatedValue = {
      ...value,
      lpFees: recalculatedFees,
      totalEqualisationFeeIncTaxLcy,
    };

    return updatedValue;
  };

  const updateSingleFeeField = (
    commitmentFees: EqualisationFeesFormValues['totalCommitmentFees'][number],
    field: 'equalisationFeeExTaxLcy' | 'equalisationSalesTaxAmountLcy',
    newValue: number,
    rowIndex: number,
  ) => {
    if (field === 'equalisationFeeExTaxLcy') {
      commitmentFees.lpFees[rowIndex].equalisationFeeExTaxLcy = newValue;
      commitmentFees.lpFees[rowIndex].equalisationFeeIncTaxLcy = round(
        newValue +
          commitmentFees.lpFees[rowIndex].equalisationSalesTaxAmountLcy,
        2,
      );
      commitmentFees.totalEqualisationFeeIncTaxLcy = round(
        sumBy(commitmentFees.lpFees, (fee) => fee.equalisationFeeIncTaxLcy),
        2,
      );
      return commitmentFees;
    } else {
      commitmentFees.lpFees[rowIndex].equalisationSalesTaxAmountLcy = newValue;
      commitmentFees.lpFees[rowIndex].equalisationFeeIncTaxLcy = round(
        commitmentFees.lpFees[rowIndex].equalisationFeeExTaxLcy + newValue,
        2,
      );
      commitmentFees.totalEqualisationFeeIncTaxLcy = round(
        sumBy(commitmentFees.lpFees, (fee) => fee.equalisationFeeIncTaxLcy),
        2,
      );
      return commitmentFees;
    }
  };

  const recalculateFromTreatmentChange = (
    value: EqualisationFeesFormValues['totalCommitmentFees'][number],
    treatment: FeesEqualisationTreatment,
    templateId?: string,
  ) => {
    if (treatment === FeesEqualisationTreatment.None) {
      const updatedValue = {
        ...value,
        feesEqualisationTreatment: treatment,
        feesEqualisationTreatmentPercentage: 0,
        lpFees: [],
        totalEqualisationFeeIncTaxLcy: 0,
      };
      return updatedValue;
    }

    if (templateId) {
      const newLpFees = value.lpFeesByTemplate?.[templateId]?.lpFees || [];
      const percentage =
        treatment === FeesEqualisationTreatment.Partial
          ? value?.feesEqualisationTreatmentPercentage || 100
          : value?.feesEqualisationTreatmentPercentage;
      const result = recalculateFees({
        value: {
          ...value,
          lpFees: newLpFees,
          feesEqualisationTreatmentPercentage: percentage,
        },
        useOriginalFeeValues: true,
        treatment,
        percentage,
      });
      return result;
    }
  };

  const recalculateFromPercentageChange = (
    value: EqualisationFeesFormValues['totalCommitmentFees'][number],
    percentage: number,
  ) => {
    const result = recalculateFees({
      value,
      useOriginalFeeValues: true,
      treatment:
        value?.feesEqualisationTreatment ?? FeesEqualisationTreatment.None,
      percentage,
    });
    return result
      ? { ...result, feesEqualisationTreatmentPercentage: percentage }
      : result;
  };

  return {
    recalculateFees,
    updateSingleFeeField,
    recalculateFromTreatmentChange,
    recalculateFromPercentageChange,
  };
};

export default useEqualiseFees;
